<template>
  <div class="creator-detail">
    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-4 py-8">
      <!-- 返回按钮 -->
      <div class="mb-6">
        <button @click="$router.go(-1)" class="flex items-center text-gray-600 hover:text-gray-800">
          <i class="fas fa-arrow-left mr-2"></i>
          返回
        </button>
      </div>

      <!-- 页面标题和通知 -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-purple-800 mb-2">{{ $store.getters.t('live_creator') }}</h1>
        <p class="text-gray-600 mb-4">
          <span class="text-green-600 font-semibold">{{ creatorInfo.socialHandle }} {{ $store.getters.t('leads') }}</span> {{ $store.getters.t('added_to_talent_pool') }}
        </p>
      </div>

      <!-- 时间戳 -->
      <div class="mb-4">
        <p class="text-sm text-gray-500">{{ $store.getters.t('lead_added_on') }} {{ creatorInfo.createdAt }}</p>
      </div>

      <!-- 创作者详情 -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <!-- 加载状态 -->
        <div v-if="loading" class="text-center py-8">
          <div class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p class="text-sm text-gray-600">{{ $store.getters.t('loading_data') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="error" class="text-center py-8">
          <i class="fas fa-exclamation-triangle text-red-500 text-2xl mb-2"></i>
          <p class="text-sm text-gray-600">{{ error }}</p>
          <button @click="loadCreatorData" class="mt-2 text-purple-600 hover:text-purple-700">
            {{ $store.getters.t('retry') }}
          </button>
        </div>

        <!-- 创作者信息头部 -->
        <div v-else class="flex items-start justify-between mb-6">
          <div class="flex items-center">
            <div class="w-20 h-20 bg-gray-200 rounded-full mr-6 flex items-center justify-center">
              <img 
                  v-if="creatorBasicInfo.avatarUrl" 
                  :src="creatorBasicInfo.avatarUrl" 
                  :alt="creatorBasicInfo.nickname || creatorBasicInfo.preferredName"
                  class="w-full h-full object-cover"
                />
              <i v-else class="fas fa-user text-gray-400 text-xl"></i>
            </div>
            <div>
              <h2 class="text-2xl font-bold mb-2">{{ creatorInfo.id }}</h2>
              <div class="flex items-center mb-1">
                <span class="text-gray-600 text-sm">{{ creatorInfo.socialHandle }}</span>
              </div>
              <div class="flex items-center">
                <span class="text-gray-800 font-semibold">{{ creatorInfo.preferredName }}</span>
                <span class="ml-1">🚀</span>
              </div>
              <div class="text-sm text-gray-500 mt-1">
                {{ creatorInfo.platform }} • {{ creatorInfo.country }} • {{ $store.getters.t('status') }}: {{ creatorInfo.status }}
              </div>
            </div>
          </div>
          
          <!-- 性能趋势图表 - 水平线连接 -->
          <div class="flex-1 ml-8">
            <div class="relative">
              <!-- 水平连接线 -->
              <div class="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-300 transform -translate-y-1/2"></div>
              
              <!-- 数据点 -->
              <div class="flex items-center justify-between relative">
                <!-- 加载状态 -->
                <div v-if="loadingLiveSessions" class="text-center relative">
                  <div class="w-3 h-3 bg-gray-400 rounded-full mx-auto relative z-10 animate-pulse"></div>
                  <p class="text-xs text-gray-500 mt-1">加载中...</p>
                </div>
                
                <!-- 空数据状态 -->
                <div v-else-if="!liveSessions || liveSessions.length === 0" class="text-center relative">
                  <div class="w-3 h-3 bg-gray-300 rounded-full mx-auto relative z-10"></div>
                  <p class="text-xs text-gray-500 mt-1">暂无数据</p>
                </div>
                
                <!-- 动态数据点 -->
                <div 
                  v-else
                  v-for="session in performanceTrendData" 
                  :key="session.sessionId"
                  class="text-center relative"
                >
                  <p class="font-bold text-lg mb-1">{{ formatNumber(session.likeCount) }}</p>
                  <div class="w-3 h-3 bg-blue-500 rounded-full mx-auto relative z-10"></div>
                  <p class="text-xs text-gray-500 mt-1">{{ formatSessionTime(session.sessionId) }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 状态标签 -->
          <div class="flex space-x-2 ml-4">
            <span class="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">{{ $store.getters.t('live_creator') }}</span>
            <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">{{ $store.getters.t('rising') }}</span>
          </div>
        </div>

        <!-- 直播数据表格 -->
        <div v-if="!loading && !error">
          <h3 class="text-lg font-bold mb-4">{{ $store.getters.t('live_stream_data') }}</h3>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b">
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('live_session_id') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('live_duration') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('total_comments') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('unique_commenters') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('live_viewers') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('gift_count') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('gift_value') }}</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700">{{ $store.getters.t('actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <!-- 加载状态 -->
                <tr v-if="loadingLiveSessions" class="border-b">
                  <td colspan="7" class="py-8 text-center">
                    <div class="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p class="text-sm text-gray-600">{{ $store.getters.t('loading_data') }}</p>
                  </td>
                </tr>
                
                <!-- 错误状态 -->
                <tr v-else-if="liveSessionsError" class="border-b">
                  <td colspan="7" class="py-8 text-center">
                    <i class="fas fa-exclamation-triangle text-red-500 text-xl mb-2"></i>
                    <p class="text-sm text-gray-600">{{ liveSessionsError }}</p>
                    <button @click="loadLiveSessions" class="mt-2 text-purple-600 hover:text-purple-700 text-sm">
                      {{ $store.getters.t('retry') }}
                    </button>
                  </td>
                </tr>
                
                <!-- 空数据状态 -->
                <tr v-else-if="!liveSessions || liveSessions.length === 0" class="border-b">
                  <td colspan="7" class="py-8 text-center">
                    <i class="fas fa-video text-gray-400 text-xl mb-2"></i>
                    <p class="text-sm text-gray-600">暂无直播数据</p>
                  </td>
                </tr>
                
                <!-- 动态数据行 -->
                <tr 
                  v-else
                  v-for="session in liveSessions" 
                  :key="session.sessionId"
                  class="border-b hover:bg-gray-50"
                >
                  <td class="py-3 px-4">{{ formatSessionTime(session.sessionId) }}</td>
                  <td class="py-3 px-4">{{ formatLiveDuration(session.liveDuration) }}</td>
                  <td class="py-3 px-4">{{ formatNumber(session.totalComments) }}</td>
                  <td class="py-3 px-4">{{ formatNumber(session.uniqueCommenters) }}</td>
                  <td class="py-3 px-4">{{ formatNumber(session.onlineUsers) }}</td>
                  <td class="py-3 px-4">{{ formatNumber(session.totalGiftQuantity) }}</td>
                  <td class="py-3 px-4">{{ formatGiftValue(session.totalGiftValue) }}</td>
                  <td class="py-3 px-4">
                    <button 
                      @click="viewHighlightMoments(creatorInfo.id, session.sessionId)"
                      class="text-purple-600 hover:text-purple-900 text-sm"
                    >
                      <i class="fas fa-star mr-1"></i>
                      {{ $store.getters.t('view_highlights') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 创作者视频信息 -->
      <div v-if="!loading && !error" class="bg-white rounded-lg shadow-md p-6 mt-8">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-bold">{{ $store.getters.t('creator_videos') }}</h3>
            <p class="text-sm text-gray-500 mt-1">{{ $store.getters.t('click_video_to_watch') }}</p>
          </div>
          <div class="flex items-center space-x-4">
            <div class="text-sm text-gray-600">
              {{ $store.getters.t('total_videos') }}: <span class="font-semibold">{{ videoStats.totalVideos }}</span>
            </div>
            <div class="text-sm text-gray-600">
              {{ $store.getters.t('total_views') }}: <span class="font-semibold">{{ formatNumber(videoStats.totalViews) }}</span>
            </div>
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="loadingVideos" class="text-center py-8">
          <div class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p class="text-sm text-gray-600">{{ $store.getters.t('loading_videos') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="videoError" class="text-center py-8">
          <i class="fas fa-exclamation-triangle text-red-500 text-2xl mb-2"></i>
          <p class="text-sm text-gray-600">{{ videoError }}</p>
          <button @click="loadVideos" class="mt-2 text-purple-600 hover:text-purple-700">
            {{ $store.getters.t('retry') }}
          </button>
        </div>

        <!-- 视频列表 -->
        <div v-else class="space-y-4">
          <div 
            v-for="video in paginatedVideos" 
            :key="video.id" 
            @click="openVideo(video)"
            class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50"
          >
            <div class="flex items-start space-x-4">
              <!-- 视频缩略图 -->
              <div class="w-32 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 relative group">
                <img 
                  v-if="video.thumbnailUrl" 
                  :src="video.thumbnailUrl" 
                  :alt="video.title"
                  class="w-full h-full object-cover rounded-lg"
                />
                <i v-else class="fas fa-play text-gray-400 text-xl"></i>
                
                <!-- 播放按钮覆盖层 -->
                <div class="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                  <i class="fas fa-play text-white text-2xl"></i>
                </div>
              </div>
              
              <!-- 视频信息 -->
              <div class="flex-1 min-w-0">
                <h4 class="font-semibold text-gray-900 mb-1 truncate">{{ video.title }}</h4>
                <p class="text-sm text-gray-600 mb-2 line-clamp-2">{{ video.description }}</p>
                
                <!-- 视频统计 -->
                <div class="flex items-center space-x-6 text-sm text-gray-500">
                  <div class="flex items-center">
                    <i class="fas fa-eye mr-1"></i>
                    <span>{{ formatNumber(video.playCount) }}</span>
                  </div>
                  <div class="flex items-center">
                    <i class="fas fa-heart mr-1"></i>
                    <span>{{ formatNumber(video.likeCount) }}</span>
                  </div>
                  <div class="flex items-center">
                    <i class="fas fa-comment mr-1"></i>
                    <span>{{ formatNumber(video.commentCount) }}</span>
                  </div>
                  <div class="flex items-center">
                    <i class="fas fa-share mr-1"></i>
                    <span>{{ formatNumber(video.shareCount) }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 视频时长和发布时间 -->
              <div class="text-right text-sm text-gray-500">
                <div class="mb-1">{{ formatDuration(video.duration) }}</div>
                <div>{{ formatDate(video.publishTime) }}</div>
                <div class="mt-1">
                  <i class="fas fa-external-link-alt text-purple-500 text-xs"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="!loadingVideos && !videoError && videos.length > 0" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-600">
            {{ $store.getters.t('showing_records') }} {{ (currentPage - 1) * pageSize + 1 }} {{ $store.getters.t('to') }} {{ Math.min(currentPage * pageSize, videos.length) }} {{ $store.getters.t('of') }} {{ videos.length }} {{ $store.getters.t('records') }}
          </div>
          <div class="flex items-center space-x-2">
            <button 
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $store.getters.t('previous_page') }}
            </button>
            <span class="px-3 py-1 text-sm">{{ currentPage }} / {{ totalPages }}</span>
            <button 
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $store.getters.t('next_page') }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import creatorService from '@/services/creatorService'

export default {
  name: 'CreatorDetailPage',
  data() {
    return {
      creatorId: '',
      creatorInfo: {},
      creatorBasicInfo: {
        avatarUrl: '',
        nickname: '',
        preferredName: '',
        socialHandle: '',
        totalVideos: 0,
        totalViews: 0
      },
      loading: false,
      error: null,
      videos: [],
      loadingVideos: false,
      videoError: null,
      liveSessions: [],
      loadingLiveSessions: false,
      liveSessionsError: null,
      currentPage: 1,
      pageSize: 10,
      videoStats: {
        totalVideos: 0,
        totalViews: 0
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.videos.length / this.pageSize)
    },
    paginatedVideos() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.videos.slice(start, end)
    },
    performanceTrendData() {
      if (!this.liveSessions || this.liveSessions.length === 0) {
        return []
      }
      
      // 按时间排序，取最新的6组数据
      const sortedSessions = [...this.liveSessions].sort((a, b) => {
        // 确保sessionId是字符串类型，然后按降序排列
        const sessionIdA = String(a.sessionId || '')
        const sessionIdB = String(b.sessionId || '')
        return sessionIdA.localeCompare(sessionIdB)
      })
      
      // 最多返回6组数据
      if(sortedSessions.length > 6){
        return sortedSessions.slice(sortedSessions.length-6, sortedSessions.length)
      }else{
        return sortedSessions.slice(0, sortedSessions.length)
      }
    }
  },
  async created() {
    // 从路由参数获取创作者ID
    this.creatorId = this.$route.params.id;
    
    // 加载创作者数据
    await this.loadCreatorData()
  },
  methods: {
    async loadCreatorData() {
      this.loading = true
      this.error = null
      
      try {
        // 使用creatorId直接获取创作者信息
        const result = await creatorService.getCreatorById(this.creatorId)
        
        if (result && result.success && result.data) {
          this.creatorInfo = result.data
          
          // 加载视频数据
          await this.loadVideos()
          
          // 加载直播会话数据
          await this.loadLiveSessions()
        } else {
          this.error = '未找到创作者信息'
        }
      } catch (error) {
        console.error('加载创作者数据失败:', error)
        this.error = '加载创作者数据失败'
      } finally {
        this.loading = false
      }
    },
    
    async loadVideos() {
      if (!this.creatorInfo.socialHandle) return
      
      this.loadingVideos = true
      this.videoError = null
      
      try {
        // 获取创作者详细数据
        const detailsData = await creatorService.getCreatorDetails([this.creatorInfo.socialHandle])
        const creatorDetail = detailsData.data?.find(item => item.username === this.creatorInfo.socialHandle)
        
        if (creatorDetail && creatorDetail.videos) {
          this.creatorBasicInfo.avatarUrl = creatorDetail.avatarUrl;
          this.creatorBasicInfo.nickname = creatorDetail.nickname;
          this.creatorBasicInfo.preferredName = creatorDetail.preferredName;
          this.creatorBasicInfo.socialHandle = creatorDetail.socialHandle;
          this.creatorBasicInfo.totalVideos = creatorDetail.totalVideos;
          this.creatorBasicInfo.totalViews = creatorDetail.totalViews;
          this.videos = creatorDetail.videos || []
          this.videoStats.totalVideos = creatorDetail.totalVideos || this.videos.length
          this.videoStats.totalViews = this.videos.reduce((sum, video) => sum + (video.playCount || 0), 0)
        } else {
          this.videos = []
          this.videoStats.totalVideos = 0
          this.videoStats.totalViews = 0
        }
      } catch (error) {
        console.error('加载视频数据失败:', error)
        this.videoError = '加载视频数据失败，请重试'
      } finally {
        this.loadingVideos = false
      }
    },

    async loadLiveSessions() {
      if (!this.creatorInfo.socialHandle) return
      
      this.loadingLiveSessions = true
      this.liveSessionsError = null
      
      try {
        // 使用socialHandle作为roomId获取直播会话数据
        const result = await creatorService.getLiveSessionSummary(this.creatorInfo.socialHandle)
        
        if (result && result.success && result.data) {
          this.liveSessions = result.data || []
        } else {
          this.liveSessionsError = '未找到直播会话数据'
        }
      } catch (error) {
        console.error('加载直播会话数据失败:', error)
        this.liveSessionsError = '加载直播会话数据失败'
      } finally {
        this.loadingLiveSessions = false
      }
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    },
    
    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toLocaleString('zh-CN')
    },
    
    formatDuration(seconds) {
      if (!seconds) return '0:00'
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    formatLiveDuration(seconds) {
      if (!seconds) return '0s'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainingSeconds = seconds % 60
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ${remainingSeconds}s`
      } else if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`
      } else {
        return `${remainingSeconds}s`
      }
    },
    
    formatGiftValue(value) {
      if (!value) return '0'
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M+'
      } else if (value >= 10000) {
        return (value / 1000).toFixed(0) + 'K+'
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K+'
      }
      return value.toLocaleString('zh-CN') + '+'
    },
    
    formatSessionTime(sessionId) {
      if (!sessionId) return '未知时间'
      
      try {
        // 确保sessionId是字符串类型
        const sessionIdStr = String(sessionId)
        
        // 处理长度为10的sessionId格式：2025083021 (年月日时)
        if (sessionIdStr.length === 10) {
          const year = sessionIdStr.substring(0, 4)
          const month = sessionIdStr.substring(4, 6)
          const day = sessionIdStr.substring(6, 8)
          const hour = sessionIdStr.substring(8, 10)
          
          return `${year}.${month}.${day} ${hour}:00`
        } else if (sessionIdStr.length >= 13) {
          // 如果是时间戳格式
          const timestamp = parseInt(sessionIdStr.substring(0, 13))
          const date = new Date(timestamp)
          return this.formatShortDate(date)
        } else {
          // 如果是其他格式，尝试解析
          return sessionIdStr.substring(0, 10) // 取前10位作为标识
        }
      } catch (error) {
        console.warn('解析sessionId时间失败:', error)
        return String(sessionId).substring(0, 10) || '未知'
      }
    },
    
    formatShortDate(date) {
      if (!date || isNaN(date.getTime())) return '未知'
      
      const now = new Date()
      const diffTime = now.getTime() - date.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) {
        return '今天'
      } else if (diffDays === 1) {
        return '昨天'
      } else if (diffDays <= 7) {
        return `${diffDays}天前`
      } else {
        const month = date.getMonth() + 1
        const day = date.getDate()
        return `${month}.${day}`
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    
    /**
     * 查看高光时刻
     */
    viewHighlightMoments(roomId, sessionId) {
      // 使用roomId作为creatorId参数，sessionId作为会话ID
      this.$router.push(`/highlight-moments/${roomId}/${sessionId}`)
    },
    
    openVideo(video) {
      if (video.videoUrl) {
        // 在新窗口中打开视频
        window.open(video.videoUrl, '_blank')
      } else {
        // 如果没有videoUrl，显示提示
        this.$store.dispatch('showNotification', '视频链接不可用')
      }
    }
  }
}
</script>

<style scoped>
.creator-detail {
  /* background: #f5f5f5; */
  background: #2a2239;
  min-height: calc(100vh - 64px);
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.gap-6 {
  gap: 1.5rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-6 {
  margin-right: 1.5rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-3 {
  padding: 0.75rem;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.py-8 {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.text-3xl {
  font-size: 1.875rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.text-xl {
  font-size: 1.25rem;
}

.text-lg {
  font-size: 1.125rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-xs {
  font-size: 0.75rem;
}

.font-bold {
  font-weight: 700;
}

.font-semibold {
  font-weight: 600;
}

.text-gray-800 {
  color: #1f2937;
}

.text-gray-700 {
  color: #374151;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-500 {
  color: #6b7280;
}

.text-green-600 {
  color: #16a34a;
}

.text-gray-400 {
  color: #9ca3af;
}

.text-yellow-800 {
  color: #92400e;
}

.text-blue-800 {
  color: #1e40af;
}

.text-blue-500 {
  color: #3b82f6;
}

.bg-white {
  background-color: #ffffff;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.bg-gray-200 {
  background-color: #e5e7eb;
}

.bg-gray-300 {
  background-color: #d1d5db;
}

.bg-yellow-100 {
  background-color: #fef3c7;
}

.bg-blue-100 {
  background-color: #dbeafe;
}

.bg-green-50 {
  background-color: #f0fdf4;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-full {
  border-radius: 9999px;
}

.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.border-b {
  border-bottom-width: 1px;
}

.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

.hover\:text-gray-800:hover {
  color: #1f2937;
}

.w-20 {
  width: 5rem;
}

.h-20 {
  height: 5rem;
}

.w-4 {
  width: 1rem;
}

.h-4 {
  height: 1rem;
}

.w-3 {
  width: 0.75rem;
}

.h-3 {
  height: 0.75rem;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.items-start {
  align-items: flex-start;
}

.justify-between {
  justify-content: space-between;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

.space-x-8 > * + * {
  margin-left: 2rem;
}

.text-center {
  text-align: center;
}

.text-left {
  text-align: left;
}

.overflow-x-auto {
  overflow-x: auto;
}

.w-full {
  width: 100%;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.my-1 {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}
</style> 